import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Circle, Bike, MapPin, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { eur, STATUS_LABELS, STATUS_COLORS, fmtDate } from '@/lib/format';
import { PublicHeader, Footer } from '@/components/Header';
import { BaseMap, Marker, Polygon, RESTAURANT_ICON, CUSTOMER_ICON, RIDER_ICON, FitBounds } from '@/components/MapBase';

const STAGES = ['pending', 'accepted', 'preparing', 'ready', 'in_delivery', 'delivered'];

export default function TrackingPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [rider, setRider] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      try {
        const [o, r, s] = await Promise.all([
          api.get(`/orders/${orderId}`),
          api.get(`/orders/${orderId}/rider-location`),
          api.get('/settings'),
        ]);
        if (mounted) { setOrder(o.data); setRider(r.data); setSettings(s.data); }
      } catch (e) {}
    };
    fetchAll();
    const iv = setInterval(fetchAll, 8000);
    return () => { mounted = false; clearInterval(iv); };
  }, [orderId]);

  if (!order) return (
    <div className="min-h-screen flex flex-col"><PublicHeader />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-10">Caricamento...</main><Footer/></div>
  );

  const currentIdx = STAGES.indexOf(order.status);
  const bounds = [];
  if (settings) bounds.push([settings.lat, settings.lng]);
  bounds.push([order.delivery_lat, order.delivery_lng]);
  if (rider?.lat != null) bounds.push([rider.lat, rider.lng]);

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Ordine #{order.order_number}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Il tuo ordine</h1>
          </div>
          <Badge className={`${STATUS_COLORS[order.status]} border rounded-full px-3 py-1`} data-testid="tracking-order-status">
            {STATUS_LABELS[order.status]}
          </Badge>
        </div>

        {order.status === 'rejected' && (
          <div className="rounded-2xl bg-[#FDECEC] text-[#9B1C1C] p-4 mb-4">L'ordine è stato rifiutato dal ristorante. Contattaci per assistenza.</div>
        )}

        {order.scheduled_for && (
          <div className="rounded-2xl bg-[#FFF6E1] border border-[#F4D89A] text-[#8A5A00] px-4 py-3 mb-4 flex items-center gap-2 text-sm">
            <span>⏰</span>
            <div>
              <b>Pre-ordine schedulato</b> per {new Date(order.scheduled_for).toLocaleString('it-IT', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-4">
            <div className="rounded-2xl bg-white border border-[--brand-border] p-5" data-testid="tracking-status-stepper">
              <div className="flex overflow-x-auto gap-4 hide-scrollbar">
                {STAGES.map((st, i) => {
                  const done = i <= currentIdx && order.status !== 'rejected';
                  return (
                    <div key={st} className="flex items-center gap-2 shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done ? 'bg-[--brand-primary] text-white' : 'bg-[--brand-surface-2] text-[--brand-muted]'}`}>
                        {done ? <CheckCircle2 size={16}/> : <Circle size={16}/>}
                      </div>
                      <div className="text-xs font-semibold whitespace-nowrap">{STATUS_LABELS[st]}</div>
                      {i < STAGES.length - 1 && <div className={`h-0.5 w-6 ${i < currentIdx ? 'bg-[--brand-primary]' : 'bg-[--brand-border]'}`}/>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="h-[380px]" data-testid="tracking-map">
              <BaseMap center={[order.delivery_lat, order.delivery_lng]} zoom={14} style={{ height: '100%' }}>
                <FitBounds bounds={bounds} />
                {settings && <Marker position={[settings.lat, settings.lng]} icon={RESTAURANT_ICON} />}
                <Marker position={[order.delivery_lat, order.delivery_lng]} icon={CUSTOMER_ICON} />
                {rider?.lat != null && rider?.lng != null && (
                  <Marker position={[rider.lat, rider.lng]} icon={RIDER_ICON} />
                )}
              </BaseMap>
            </div>

            {order.assigned_rider_name && (
              <div className="rounded-2xl bg-white border border-[--brand-border] p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[--brand-accent]/15 flex items-center justify-center text-[--brand-accent]"><Bike size={20}/></div>
                <div className="flex-1">
                  <div className="font-semibold">Rider assegnato</div>
                  <div className="text-sm text-[--brand-muted]">{order.assigned_rider_name}</div>
                </div>
                {rider?.updated_at && <div className="text-xs text-[--brand-muted]">Aggiornato {fmtDate(rider.updated_at)}</div>}
              </div>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-4">
            <div className="rounded-2xl bg-white border border-[--brand-border] p-5">
              <h3 className="font-display font-bold mb-3">Dettagli</h3>
              <div className="space-y-2 text-sm">
                <Row label="Consegna" value={order.delivery_address}/>
                <Row label="Telefono" value={order.customer_phone}/>
                <Row label="Pagamento" value={order.payment_method === 'cash' ? 'Contanti' : 'Carta'}/>
                <Row label="Ordinato" value={fmtDate(order.created_at)}/>
              </div>
              <div className="border-t border-[--brand-border] my-3"/>
              <div className="space-y-1 text-sm">
                {order.items.map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{it.quantity}× {it.name}</span>
                    <span className="tabular-nums">{eur(it.unit_price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[--brand-border] my-3"/>
              <Row label="Subtotale" value={eur(order.subtotal)}/>
              <Row label="Consegna" value={eur(order.delivery_fee)}/>
              <div className="flex justify-between mt-2">
                <span className="font-display font-bold">Totale</span>
                <span className="font-display font-bold text-lg tabular-nums text-[--brand-primary]">{eur(order.total)}</span>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[--brand-muted]">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
